import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-invite-modal',
  templateUrl: './invite-modal.component.html',
  styleUrls: ['./invite-modal.component.scss']
})
export class InviteModalComponent implements OnInit {
  @Input() isOpen: boolean = false;
  @Input() pathwayId: string = '';
  @Input() existingInvites: any[] = [];
  @Input() isEditMode: boolean = false; // New input to determine if it's edit mode
  @Output() modalClosed = new EventEmitter<any>();
  @Output() invitesSent = new EventEmitter<any>();

  // Invite properties
  newInviteEmail: string = '';
  newInvitedEmails: any[] = [];
  inviteMessage: string = '';
  inviteError: string = '';
  selectedAccessType: string = 'viewer';

  readonly accessTypeOptions = [
    { value: 'viewer', label: 'Viewer' },
    { value: 'editor', label: 'Editor' },
  ];

  readonly accessTypeFullOptions = [
    { value: 'viewer', label: 'Viewer - Can only view the Tangram' },
    { value: 'editor', label: 'Editor - Can view and edit the Tangram' },
  ];
  
  // Layout properties
  isGoogleStyleLayout: boolean = true; // Start with Google-style layout
  isAddingNewEmail: boolean = false;
  
  // Change tracking
  hasChanges: boolean = false;
  originalInvites: any[] = [];
  changedCount: number = 0;

  constructor(
    private modalService: NgbModal,
    private toasterService: ToastrService
  ) {}

  ngOnInit() {
    // Set layout based on edit mode
    this.isGoogleStyleLayout = this.isEditMode;
    
    // Initialize with existing invites if any (for Google-style layout)
    if (this.existingInvites && this.existingInvites.length > 0) {
      // Store original invites for comparison
      this.originalInvites = JSON.parse(JSON.stringify(this.existingInvites));
    }
    
    // Initialize new invited emails as empty array for original layout
    this.newInvitedEmails = [];
    
    // Initialize with Save (0)
    this.changedCount = 0;
    this.hasChanges = false;
  }

  /**
   * Switch to original layout when user starts adding new email
   */
  switchToOriginalLayout() {
    this.isGoogleStyleLayout = false;
    this.isAddingNewEmail = true;
  }

  /**
   * Switch back to Google-style layout
   */
  switchToGoogleLayout() {
    this.isGoogleStyleLayout = true;
    this.isAddingNewEmail = false;
  }

  /**
   * Handle input focus - switch to original layout
   */
  onInputFocus() {
    console.log('this.isEditMode && this.isGoogleStyleLayout: ', this.isEditMode && this.isGoogleStyleLayout);
    if (this.isEditMode && this.isGoogleStyleLayout) {
      this.switchToOriginalLayout();
    }
  }

  /**
   * Add email to new invite list
   */
  addInviteEmail() {
    if (this.newInviteEmail && this.newInviteEmail.trim()) {
      const email = this.newInviteEmail.trim().toLowerCase();
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.inviteError = 'Please enter a valid email address';
        return;
      }
      
      // Check if email already exists in new invite list
      if (this.newInvitedEmails.some(item => item.email === email)) {
        this.inviteError = 'This email is already in the list';
        return;
      }
      
      // Check if email already exists in existing invites
      if (this.existingInvites && this.existingInvites.some(item => item.email === email)) {
        this.inviteError = 'This email already has access to this Tangram';
        return;
      }
      
      this.newInvitedEmails.push({
        email: email,
        accessType: this.selectedAccessType
      });
      this.newInviteEmail = '';
      this.inviteError = '';
      
      // Switch back to Google-style layout after adding email
      // this.switchToGoogleLayout();
    }
  }

  /**
   * Remove email from new invite list
   */
  removeNewInviteEmail(index: number) {
    this.newInvitedEmails.splice(index, 1);
  }

  /**
   * Remove invited email from existing invites list with alert popup
   */
  removeInvitedEmail(index: number) {
    const emailToRemove = this.existingInvites[index];
    if (emailToRemove) {
      // Show alert popup instead of confirm dialog
      this.showRemoveConfirmation(emailToRemove.email, index);
    }
  }

  /**
   * Show remove confirmation alert popup
   */
  showRemoveConfirmation(email: string, index: number) {
    // This will be handled by the parent component
    // Emit event to show alert popup
    this.modalClosed.emit({
      action: 'removeConfirmation',
      email: email,
      index: index
    });
  }

  /**
   * Handle access type change for existing invites
   */
  onAccessTypeChange(index: number, newAccessType: string) {
    if (this.existingInvites[index]) {
      this.existingInvites[index].accessType = newAccessType;
      this.checkForChanges();
    }
  }

  /**
   * Check if there are any changes from the original state
   */
  checkForChanges() {
    // Count access type changes
    let accessTypeChanges = 0;
    this.existingInvites.forEach(current => {
      const original = this.originalInvites.find(orig => orig.email === current.email);
      if (original && original.accessType !== current.accessType) {
        accessTypeChanges++;
      }
    });
    
    this.changedCount = accessTypeChanges;
    this.hasChanges = this.changedCount > 0;
  }

  /**
   * Remove email from list (called after confirmation)
   */
  removeEmailFromList(index: number) {
    this.existingInvites.splice(index, 1);
    this.checkForChanges();
  }

  /**
   * Send invitations
   */
  sendInvites() {
    if (this.newInvitedEmails.length === 0) {
      this.inviteError = 'Please add at least one email address';
      return;
    }

    // Prepare invite data
    const inviteData = {
      emails: this.newInvitedEmails,
      message: this.inviteMessage,
      isGoogleStyleLayout: this.isGoogleStyleLayout
    };

    this.invitesSent.emit(inviteData);
    // this.toasterService.success(`Invitations prepared for ${this.newInvitedEmails.length} people!`);
  }

  /**
   * Update access types for Google-style layout
   */
  updateAccessTypes() {
    if (!this.hasChanges) {
      this.inviteError = 'No changes to save';
      return;
    }

    // Prepare access update data with only changed items
    const changedInvites = this.existingInvites.filter(current => {
      const original = this.originalInvites.find(orig => orig.email === current.email);
      return original && original.accessType !== current.accessType;
    });

    const accessUpdateData = {
      emails: changedInvites,
      message: this.inviteMessage,
      pathwayId: this.pathwayId,
      isGoogleStyleLayout: this.isGoogleStyleLayout
    };

    this.invitesSent.emit(accessUpdateData);
  }

  /**
   * Close modal
   */
  closeModal() {
    this.modalClosed.emit();
  }

  /**
   * Handle Enter key press
   */
  // onKeyPress(event: KeyboardEvent) {
  //   // Use event.key if available, otherwise fallback to event.keyCode for mobile compatibility
  //   const key = event.key || String.fromCharCode(event.keyCode || event.which);
  //   const keyCode = event.keyCode || event.which;
    
  //   // Check for Enter key (13) or Enter key string
  //   if (key === 'Enter' || keyCode === 13) {
  //     this.addInviteEmail();
  //   }
  // }

  /**
   * Handle keydown events with better mobile support
   */
  onKeyDown(event: KeyboardEvent) {
    // For mobile devices, prioritize keyCode over event.key
    const key = event.key;
    const keyCode = event.keyCode || event.which;
    console.log('keyCode: ', keyCode, 'key: ', key);
    
    // Handle Enter key
    if (key === 'Enter' || keyCode === 13) {
      this.addInviteEmail();
      event.preventDefault();
      return;
    }
    
    // Handle Backspace key to remove previous chip email
    if (key === 'Backspace' || keyCode === 8) {
      this.handleBackspaceKey();
      return;
    }
    
    // For mobile devices, use keyCode primarily
    // Desktop: space=32, comma=44, semicolon=59
    // Mobile: space=229 (composition key), comma=188, semicolon=186
    // Additional mobile keyCodes: period=190, slash=191
    if (key === ' ' || key === ',' || key === ';' || 
        keyCode === 32 || keyCode === 44 || keyCode === 59 || // Desktop keyCodes
        keyCode === 229 || keyCode === 188 || keyCode === 186) { // Mobile keyCodes
      this.tryAddEmailFromInput();
      event.preventDefault();
      return;
    }
  }

  /**
   * Handle input change events (for mobile support)
   */
  onInputChange() {
    // Check if input contains multiple emails separated by space, comma, or semicolon
    if (this.newInviteEmail && this.newInviteEmail.trim()) {
      const emails = this.newInviteEmail.split(/[\s,;]+/).filter(email => email.trim());
      
      if (emails.length > 1) {
        // Process all emails except the last one (which might be incomplete)
        for (let i = 0; i < emails.length - 1; i++) {
          const email = emails[i].trim().toLowerCase();
          if (this.isValidEmail(email)) {
            this.addEmailToNewList(email);
          }
        }
        // Keep the last email in the input field
        this.newInviteEmail = emails[emails.length - 1].trim();
      } else {
        // Check if the input ends with a space and contains a valid email
        const trimmedInput = this.newInviteEmail.trim();
        if (this.newInviteEmail.endsWith(' ') && this.isValidEmail(trimmedInput)) {
          // Add the email and clear the input
          this.addEmailToNewList(trimmedInput);
          this.newInviteEmail = '';
        }
      }
    }
  }

  /**
   * Try to add email from current input
   */
  tryAddEmailFromInput() {
    if (this.newInviteEmail && this.newInviteEmail.trim()) {
      const email = this.newInviteEmail.trim().toLowerCase();
      if (this.isValidEmail(email)) {
        this.addEmailToNewList(email);
        this.newInviteEmail = '';
        this.inviteError = '';
      }
    }
  }

  /**
   * Handle backspace key to remove previous chip email
   */
  handleBackspaceKey() {
    // Only remove chip if input field is empty
    if (!this.newInviteEmail || this.newInviteEmail.trim() === '') {
      // In Google-style layout, remove from existing invites first
      if (this.isGoogleStyleLayout && this.existingInvites.length > 0) {
        // Show confirmation for existing invites
        this.showRemoveConfirmation(this.existingInvites[this.existingInvites.length - 1].email, this.existingInvites.length - 1);
      } else if (this.newInvitedEmails.length > 0) {
        // Remove the last added email chip
        this.newInvitedEmails.splice(this.newInvitedEmails.length - 1, 1);
        this.inviteError = '';
      }
    }
  }

  /**
   * Check if email is valid
   */
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Add email to new list if it doesn't already exist
   */
  addEmailToNewList(email: string) {
    // Check if email already exists in new invite list
    if (!this.newInvitedEmails.some(item => item.email === email)) {
      // Check if email already exists in existing invites
      if (!this.existingInvites || !this.existingInvites.some(item => item.email === email)) {
        this.newInvitedEmails.push({
          email: email,
          accessType: this.selectedAccessType
        });
      } else {
        this.inviteError = 'This email already has access to this Tangram';
      }
    } else {
      this.inviteError = 'This email is already in the list';
    }
  }
}
